<template>
    <Layout>
        <template #form>
            <Form @submit="onSubmit" :show-feedback="showFeedback" show-go-to-login>
                <template #title>
                    {{ $t('title') }}
                </template>
                <template #subtitle>
                    {{ $t('subtitle') }}
                </template>
                <template #fields>
                    <InputUsername
                        v-model="username"
                        :state="validateField('username')"
                        :invalid-feedback="usernameInvalidFeedback"
                        autofocus
                    />
                    <Captcha @ready="captchaReady = $event" @captcha="captcha = $event" />
                </template>
                <template #button>
                    <BtnSubmit recover-password :loading="loading" :disabled="!captchaReady" />
                </template>
            </Form>
        </template>
    </Layout>
</template>

<script>
import api from '@/services/api'
import oauth from '@/services/oauth-api'
import AuthService from '@/services/auth'
import { validationMixin } from '@/mixins'
import { required, email, or } from 'vuelidate/lib/validators'
import { cpf } from '@/lib/validators'
import { onlyNumbers } from '@/lib/utils'
import { Layout, Form, InputUsername, BtnSubmit, FeedbackAuth, Captcha } from './components'
import { alertModalMixin } from './mixins'
import { get } from 'vuex-pathify'

const initialState = {
    username: null,
    token: null,
}

export default {
    name: 'ForgotPassword',
    mixins: [validationMixin, alertModalMixin],
    components: {
        Layout,
        Form,
        InputUsername,
        BtnSubmit,
        FeedbackAuth,
        Captcha,
    },
    data() {
        return {
            username: initialState.username,
            token: initialState.token,
            loading: false,
            captchaReady: false,
            captcha: null,
        }
    },
    validations: {
        username: {
            required,
            or: or(email, cpf),
        },
    },
    methods: {
        onSubmit() {
            this.token = this.getToken()
            if (this.validate() && this.token) {
                this.loading = true
                if (email(this.username)) this.goWithEmail()
                if (cpf(this.username)) this.goWithCpf()
            }
        },
        async goWithEmail() {
            try {
                const email = await AuthService(api, oauth).forgotPassword(
                    this.username,
                    this.token,
                    this.currentLocale
                )
                this.modalSuccess(this.$t('resetPasswordEmailSent', { email }))
            } catch (e) {
                this.modalSuccess(this.$t('resetPasswordEmailSent', { email: this.username }))
            } finally {
                this.reset()
                this.loading = false
            }
        },
        async goWithCpf() {
            try {
                const cpf = onlyNumbers(this.username)
                const email = await AuthService(api, oauth).forgotPassword(cpf, this.token, this.currentLocale, true)
                this.modalSuccess(this.$t('resetPasswordEmailSent', { email }))
            } catch (e) {
                this.modalError(this.$t('cpfNotFound'))
            } finally {
                this.reset()
                this.loading = false
            }
        },
        getToken() {
            return this.captcha.getResponse()
        },
        reset() {
            this.username = initialState.username
            this.token = initialState.token
            this.$v.$reset()
            this.captcha.reset()
        },
    },
    computed: {
        showFeedback() {
            return this.showFeedbackEmail || this.showFeedbackCpf
        },
        usernameInvalidFeedback() {
            if (!this.$v.username.required) return this.$t('required')
            if (!this.$v.username.or) return this.$t('emailOrCpf')
        },
        currentLocale: get('locale/currentLocale'),
    },
    i18n: {
        messages: {
            'en-US': {
                title: 'Recover your password',
                subtitle: 'You will receive a link with a 24-hour validity',
                resetPasswordEmailSent: 'Password reset link successfully sent to the email: <b>{email}</b>',
                cpfNotFound:
                    'Brazilian CPF not found . If your Brazilian CPF is not registered, try using the corporate email.',
                required: 'Required field',
                emailOrCpf: 'Invalid email address',
            },
            'es-ES': {
                title: 'Recupere su contraseña',
                subtitle: 'Recibirá un enlace con una validez de 24 horas',
                resetPasswordEmailSent:
                    'Enlace de restablecimiento de contraseña enviado con éxito al correo electrónico: <b>{email}</b>',
                cpfNotFound:
                    'CPF brasileño no encontrado. Si tu CPF brasileño no está registrado, intenta usar el correo corporativo.',
                required: 'Campo obligatorio',
                emailOrCpf: 'Dirección de correo electrónico inválida',
            },
            'pt-BR': {
                title: 'Recupere sua senha',
                subtitle: 'Você receberá um link com 24h de validade',
                resetPasswordEmailSent: 'Link de redefinição de senha enviado com sucesso para o email: <b>{email}</b>',
                cpfNotFound: 'CPF não encontrado. Caso seu CPF não esteja cadastrado, tente usar o email corporativo.',
                required: 'Preenchimento obrigatório',
                emailOrCpf: 'Informe um email ou CPF válido',
            },
        },
    },
}
</script>
